/** Seller dashboard deep links (same origin as the vendor app). */
export const buildSellerDashboardLinks = (origin = window.location.origin) => ({
  home: `${origin}/dashboard`,
  products: `${origin}/dashboard/product/list`,
  productCreate: `${origin}/dashboard/product/create`,
  subscriptions: `${origin}/dashboard/Subscriptions/list`,
  sales: `${origin}/dashboard/sale/list`,
  orders: `${origin}/dashboard/order/list`,
  chats: `${origin}/dashboard/chat/list`,
  blog: `${origin}/dashboard/blog/myPosts`,
  printful: `${origin}/dashboard/printful/auth`,
  account: `${origin}/dashboard/user/account`,
  login: `${origin}/auth/login`,
});

export const buildSellerDashboardContext = (pathname) => {
  const origin = window.location.origin;
  const links = buildSellerDashboardLinks(origin);
  return {
    brand: 'AIRStudio',
    currentPageUrl: `${origin}${pathname}`,
    currentPath: pathname,
    links,
  };
};

const SECTION_DEFINITIONS = [
  {
    linkKey: 'productCreate',
    label: 'Add product',
    phrases: ['add product', 'new product', 'create product', 'product create'],
    keywords: ['addproduct', 'newproduct', 'createproduct'],
    priority: 10,
  },
  {
    linkKey: 'subscriptions',
    label: 'Subscription',
    phrases: ['subscription', 'subscriptions', 'billing', 'plan'],
    stems: ['subscr'],
    keywords: ['subscription', 'subscriptions', 'subcription', 'subscripion', 'subsrcyption'],
    priority: 9,
  },
  {
    linkKey: 'sales',
    label: 'Sales & promotions',
    phrases: ['sales', 'sale list', 'promotion', 'promotions', 'discount', 'discounts'],
    keywords: ['sales', 'sale', 'promotion', 'promotions', 'discount', 'discounts'],
    priority: 8,
  },
  {
    linkKey: 'orders',
    label: 'Orders',
    phrases: ['orders', 'order list'],
    keywords: ['orders', 'order', 'ordrs', 'ordes'],
    priority: 8,
  },
  {
    linkKey: 'chats',
    label: 'Customer chats',
    phrases: ['customer chat', 'customer chats', 'messages', 'inbox'],
    keywords: ['chats', 'chat', 'messages', 'message', 'inbox'],
    priority: 7,
  },
  {
    linkKey: 'blog',
    label: 'Blog',
    phrases: ['blog post', 'blog posts', 'my posts'],
    keywords: ['blog', 'blogs', 'posts', 'post'],
    priority: 7,
  },
  {
    linkKey: 'printful',
    label: 'Printful',
    phrases: ['print on demand', 'printful'],
    keywords: ['printful', 'printfuls'],
    priority: 7,
  },
  {
    linkKey: 'account',
    label: 'Account settings',
    phrases: ['account settings', 'profile settings'],
    keywords: ['account', 'accounts', 'profile', 'settings', 'setting'],
    priority: 6,
  },
  {
    linkKey: 'products',
    label: 'Product catalog',
    phrases: ['product catalog', 'product list', 'products list'],
    keywords: ['products', 'product', 'catalog', 'prodcut', 'produt'],
    priority: 5,
  },
  {
    linkKey: 'home',
    label: 'Dashboard home',
    phrases: ['dashboard home', 'main dashboard', 'seller dashboard home'],
    keywords: ['homepage'],
    priority: 4,
  },
];

const levenshtein = (a, b) => {
  const left = String(a || '').toLowerCase();
  const right = String(b || '').toLowerCase();
  if (left === right) return 0;
  if (!left.length) return right.length;
  if (!right.length) return left.length;

  const matrix = Array.from({ length: left.length + 1 }, () => new Array(right.length + 1).fill(0));
  for (let i = 0; i <= left.length; i += 1) matrix[i][0] = i;
  for (let j = 0; j <= right.length; j += 1) matrix[0][j] = j;

  for (let i = 1; i <= left.length; i += 1) {
    for (let j = 1; j <= right.length; j += 1) {
      const cost = left[i - 1] === right[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,
        matrix[i][j - 1] + 1,
        matrix[i - 1][j - 1] + cost
      );
    }
  }
  return matrix[left.length][right.length];
};

const STOP_WORDS = new Set([
  'share',
  'send',
  'give',
  'show',
  'provide',
  'please',
  'dashboard',
  'seller',
  'panel',
  'link',
  'links',
  'url',
  'urls',
  'address',
  'path',
  'page',
  'this',
  'current',
  'here',
  'can',
  'you',
  'the',
  'your',
  'what',
  'where',
  'open',
]);

const fuzzyDistance = (word, keyword) => {
  const w = String(word || '').toLowerCase();
  const k = String(keyword || '').toLowerCase();
  if (!w || !k) return Number.POSITIVE_INFINITY;
  if (w === k) return 0;
  if (k.length >= 4 && w.length >= k.length && (w.startsWith(k) || w.endsWith(k))) return 0;
  if (w.length >= 4 && k.length >= w.length && (k.startsWith(w) || k.endsWith(w))) return 0;
  if (k.length >= 5 && w.startsWith(k.slice(0, 5))) return 1;
  if (w.length >= 5 && k.startsWith(w.slice(0, 5))) return 1;
  return levenshtein(w, k);
};

const fuzzyThreshold = (keyword) => Math.max(2, Math.floor(String(keyword).length * 0.34));

const tokenize = (text) =>
  String(text || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(Boolean);

/** Detect dashboard section even when seller misspells the name. */
export const matchDashboardSection = (text) => {
  const raw = String(text || '');
  const lower = raw.toLowerCase();

  const phraseMatch = SECTION_DEFINITIONS.find((section) =>
    section.phrases?.some((phrase) => lower.includes(phrase))
  );
  if (phraseMatch) return phraseMatch;

  const stemMatch = SECTION_DEFINITIONS.find((section) =>
    section.stems?.some((stem) => lower.includes(stem))
  );
  if (stemMatch) return stemMatch;

  const words = tokenize(raw).filter((word) => word.length >= 4 && !STOP_WORDS.has(word));
  const fuzzyMatch = words.reduce(
    (acc, word) => {
      SECTION_DEFINITIONS.forEach((section) => {
        (section.keywords || []).forEach((keyword) => {
          const distance = fuzzyDistance(word, keyword);
          if (distance <= fuzzyThreshold(keyword) && distance < acc.bestScore) {
            acc.bestScore = distance;
            acc.best = section;
          }
        });
      });
      return acc;
    },
    { best: null, bestScore: Number.POSITIVE_INFINITY }
  );

  if (fuzzyMatch.best) return fuzzyMatch.best;

  const compact = lower.replace(/\s+/g, '');
  const compactMatch = SECTION_DEFINITIONS.find((section) =>
    (section.keywords || []).some(
      (keyword) => keyword.length >= 7 && compact.includes(keyword.slice(0, 7))
    )
  );
  if (compactMatch) return compactMatch;

  return null;
};

const isCurrentPageRequest = (text) =>
  /\b(current|this page|here|this section|where i am|page i am on)\b/i.test(String(text || ''));

export const isDashboardUrlQuestion = (text) => {
  const t = String(text || '');
  const section = matchDashboardSection(t);

  if (section && /\b(url|link|address|path)\b/i.test(t)) {
    return true;
  }

  if (section && /\b(share|send|give|show|provide)\b/i.test(t)) {
    return true;
  }

  const lower = t.toLowerCase();
  return (
    /dashboard.*(url|link|address|path)/i.test(lower) ||
    /(url|link|address|path).*(dashboard|seller|panel|this page)/i.test(lower) ||
    /share.*(url|link)/i.test(lower) ||
    /what.*(url|link).*(dashboard|page|here)/i.test(lower) ||
    /where.*(dashboard|seller panel)/i.test(lower) ||
    /\b(give|send|show|provide|share)\b.*\b(url|link)\b/i.test(lower)
  );
};

const wantsAllLinks = (text) =>
  /\b(all|every|full|complete)\b.*\b(links?|urls?|addresses?)\b/i.test(String(text || '')) ||
  /\b(links?|urls?)\b.*\b(all|every)\b/i.test(String(text || ''));

/** Pick the single dashboard URL the seller asked for. */
export const resolveDashboardUrlRequest = (text, pathname) => {
  const ctx = buildSellerDashboardContext(pathname);
  const { links, currentPageUrl } = ctx;
  const t = String(text || '');

  if (wantsAllLinks(t)) {
    return { type: 'all', ctx };
  }

  const section = matchDashboardSection(t);
  if (section) {
    return {
      type: 'single',
      label: section.label,
      url: links[section.linkKey],
    };
  }

  if (isCurrentPageRequest(t) || /dashboard.*(url|link)/i.test(t) || isDashboardUrlQuestion(t)) {
    return {
      type: 'single',
      label: 'Current page',
      url: currentPageUrl,
    };
  }

  return {
    type: 'single',
    label: 'Current page',
    url: currentPageUrl,
  };
};

export const buildDashboardUrlReply = (text, pathname) => {
  const resolved = resolveDashboardUrlRequest(text, pathname);

  if (resolved.type === 'all') {
    const { links, currentPageUrl } = resolved.ctx;
    return `Here are your **AIRStudio Seller Dashboard** links:

**Current page**
${currentPageUrl}

**Quick links**
${links.home}
${links.products}
${links.productCreate}
${links.subscriptions}
${links.sales}
${links.orders}
${links.chats}
${links.blog}
${links.printful}
${links.account}

You can bookmark or share any link above.`;
  }

  const { label, url } = resolved;
  return `Sure — here is your **AIRStudio** ${label} page:

${url}

Open or share this link anytime.`;
};
